import DOMPurify from 'dompurify';
import { linkificar } from './linkificar';

// Sanitização do HTML de mensagem de chat.
//
// A API grava o campo `message` verbatim (`MessageService::store()` não
// filtra) e `POST /api/messages` não exige autenticação — é este widget, que é
// público. Tudo o que trafega pela sala chega aqui como HTML e vai direto para
// `dangerouslySetInnerHTML`.
//
// Sem filtro, um `<img src=x onerror="...">` gravado na sala executa no
// navegador de quem abrir o chat. `<script>` não roda quando inserido via
// innerHTML, mas handlers de evento rodam — então escapar só a tag `script`
// não resolveria.
//
// A sanitização é feita na RENDERIZAÇÃO, não na gravação: o que está no banco
// continua intacto (inclusive o que veio da URA e do Smclick), e a barreira
// vale também para as mensagens que já estão gravadas.
//
// Mesmo nome e mesmo comportamento do arquivo homônimo no operatorweb, de
// propósito: é a mesma decisão, tomada uma vez.

/**
 * Devolve o HTML pronto para `dangerouslySetInnerHTML`.
 *
 * @param {string} html conteúdo a exibir
 * @returns {string} HTML sem scripts, handlers de evento ou URLs perigosas
 */
export const sanitizeHtml = (html) => {
  if (!html) return '';

  const limpo = DOMPurify.sanitize(String(html));

  // Links abrem em nova aba para não perder o estado do chat (socket/sessão)
  // ao navegar. Depois da sanitização, de propósito: o DOMPurify remove
  // `target` do conteúdo de entrada, e é aqui que ele volta — decidido por
  // nós, e sempre com o `rel` que fecha o acesso ao `window.opener`.
  const container = document.createElement('div');
  container.innerHTML = limpo;
  container.querySelectorAll('a').forEach((link) => {
    link.setAttribute('target', '_blank');
    link.setAttribute('rel', 'noopener noreferrer');
  });

  return container.innerHTML;
};

/**
 * O conteúdo de uma MENSAGEM DE CONVERSA, pronto para renderizar.
 *
 * Sanitiza e, em cima disso, transforma em link o endereço que a pessoa
 * digitou como texto (ponto 1 da Gisele: "links enviados ao cliente devem
 * aparecer como clicáveis"). O `<a href>` já funcionava; o que não funcionava
 * era o caso comum -- ninguém digita HTML numa conversa, então o endereço que
 * o operador cola chega aqui como texto e o cliente tem de copiar à mão.
 *
 * A ordem é obrigatória, e está explicada em `linkificar`: ele CRIA `<a>`, e o
 * `href` que ele monta não passaria pelo DOMPurify se viesse antes.
 *
 * @param {string} html conteúdo da mensagem, como veio da API
 * @returns {string} HTML seguro, com os endereços clicáveis
 */
export const sanitizeChatMessage = (html) => linkificar(sanitizeHtml(html));

export default sanitizeHtml;
